/**
 * 🎯 Create/Edit Filter Modal Component
 * Modal dialog for creating and editing filter options with validation
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { filterManagementService } from '@/services/filterManagementService';
import {
  FilterOption,
  FilterCategory,
  CreateFilterOptionDTO,
  UpdateFilterOptionDTO,
  FilterStatus,
  FilterFormState,
  FilterFormErrors,
} from '@/types/filters';
import toast from 'react-hot-toast';

// ============================================
// Component Props
// ============================================

interface CreateFilterModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingFilter?: FilterOption | null;
  categories: FilterCategory[];
}

// ============================================
// Helper Functions
// ============================================

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const validateForm = (formData: FilterFormState, isEditMode: boolean = false): FilterFormErrors => {
  const errors: FilterFormErrors = {};

  // Category is only required when creating, not when editing
  if (!isEditMode && !formData.category_id) {
    errors.category_id = 'Category is required';
  }

  if (!formData.name.trim()) {
    errors.name = 'Name is required';
  } else if (formData.name.length < 2) {
    errors.name = 'Name must be at least 2 characters';
  } else if (formData.name.length > 100) {
    errors.name = 'Name must be less than 100 characters';
  }

  if (!formData.slug.trim()) {
    errors.slug = 'Slug is required';
  } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
    errors.slug = 'Slug must contain only lowercase letters, numbers, and hyphens';
  }

  if (formData.description && formData.description.length > 500) {
    errors.description = 'Description must be less than 500 characters';
  }

  if (formData.display_order < 0) {
    errors.display_order = 'Display order must be non-negative';
  }

  if (formData.scheduled_publish_at && formData.scheduled_unpublish_at) {
    const publishDate = new Date(formData.scheduled_publish_at);
    const unpublishDate = new Date(formData.scheduled_unpublish_at);
    if (unpublishDate <= publishDate) {
      errors.scheduled_unpublish_at = 'Unpublish date must be after publish date';
    }
  }

  return errors;
};

// ============================================
// Main Component
// ============================================

export const CreateFilterModal: React.FC<CreateFilterModalProps> = ({
  open,
  onClose,
  onSuccess,
  editingFilter,
  categories,
}) => {
  const isEditMode = !!editingFilter;

  // Form State
  const [formData, setFormData] = useState<FilterFormState>({
    category_id: '',
    name: '',
    slug: '',
    description: '',
    icon: '',
    display_order: 0,
    status: FilterStatus.DRAFT,
    metadata: {},
    is_featured: false,
    scheduled_publish_at: null,
    scheduled_unpublish_at: null,
  });

  const [errors, setErrors] = useState<FilterFormErrors>({});
  const [loading, setLoading] = useState(false);
  const [autoGenerateSlug, setAutoGenerateSlug] = useState(true);

  // ============================================
  // Effects
  // ============================================

  useEffect(() => {
    if (open) {
      if (editingFilter) {
        // Populate form with existing filter data
        setFormData({
          category_id: editingFilter.categoryId,
          name: editingFilter.optionLabel,
          slug: editingFilter.optionKey,
          description: editingFilter.optionDescription || '',
          icon: editingFilter.iconName || '',
          display_order: editingFilter.displayOrder,
          status: editingFilter.status,
          metadata: {},
          is_featured: false,
          scheduled_publish_at: editingFilter.publishedAt,
          scheduled_unpublish_at: editingFilter.unpublishedAt,
        });
        setAutoGenerateSlug(false);
      } else {
        // Reset form for new filter
        setFormData({
          category_id: '',
          name: '',
          slug: '',
          description: '',
          icon: '',
          display_order: 0,
          status: FilterStatus.DRAFT,
          metadata: {},
          is_featured: false,
          scheduled_publish_at: null,
          scheduled_unpublish_at: null,
        });
        setAutoGenerateSlug(true);
      }
      setErrors({});
    }
  }, [open, editingFilter]);

  // ============================================
  // Event Handlers
  // ============================================

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      slug: autoGenerateSlug ? generateSlug(value) : prev.slug,
    }));
  };

  const handleSlugChange = (value: string) => {
    setAutoGenerateSlug(false);
    setFormData((prev) => ({ ...prev, slug: value }));
  };

  const handleSubmit = async () => {
    // Validate form
    const validationErrors = validateForm(formData, isEditMode);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      if (isEditMode && editingFilter) {
        // Update existing filter
        const updateData: UpdateFilterOptionDTO = {
          optionLabel: formData.name,
          optionDescription: formData.description || null,
          iconName: formData.icon || null,
          displayOrder: formData.display_order,
          publishedAt: formData.scheduled_publish_at,
          unpublishedAt: formData.scheduled_unpublish_at,
        };

        const response = await filterManagementService.updateFilterOption(editingFilter.id, updateData);

        if (response.success) {
          toast.success('Filter option updated successfully');
          onSuccess();
          onClose();
        } else {
          toast.error(response.message || 'Failed to update filter option');
        }
      } else {
        // Create new filter
        const createData: CreateFilterOptionDTO = {
          categoryId: formData.category_id,
          optionKey: formData.slug,
          optionLabel: formData.name,
          optionDescription: formData.description || null,
          iconName: formData.icon || null,
          displayOrder: formData.display_order,
          isGlobal: true,
          publishedAt: formData.scheduled_publish_at,
          unpublishedAt: formData.scheduled_unpublish_at,
        };

        const response = await filterManagementService.createFilterOption(createData);

        if (response.success) {
          toast.success('Filter option created successfully');
          onSuccess();
          onClose();
        } else {
          toast.error(response.message || 'Failed to create filter option');
        }
      }
    } catch (error: any) {
      console.error('Failed to save filter option:', error);
      toast.error('Failed to save filter option');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.categoryName || category?.name || 'Unknown';
  };

  // ============================================
  // Render
  // ============================================

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Filter Option' : 'Create New Filter Option'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Info Alert */}
          <Alert severity="info">
            Filter options allow users to narrow down search results. Create clear, descriptive options
            that help users find exactly what they're looking for.
          </Alert>

          {/* Category Selection */}
          <FormControl fullWidth error={!!errors.category_id}>
            <InputLabel>Category *</InputLabel>
            <Select
              value={formData.category_id}
              label="Category *"
              onChange={(e) => setFormData((prev) => ({ ...prev, category_id: e.target.value }))}
              disabled={loading}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.categoryName || cat.name}
                </MenuItem>
              ))}
            </Select>
            {errors.category_id && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.category_id}
              </Typography>
            )}
          </FormControl>

          {/* Name */}
          <TextField
            label="Name"
            required
            fullWidth
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'Display name for the filter option'}
            disabled={loading}
          />

          {/* Slug */}
          <TextField
            label="Slug"
            required
            fullWidth
            value={formData.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            error={!!errors.slug}
            helperText={
              errors.slug ||
              (autoGenerateSlug
                ? 'Auto-generated from name (edit to customize)'
                : 'URL-friendly identifier (lowercase, hyphens only)')
            }
            disabled={loading}
          />

          {/* Description */}
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            error={!!errors.description}
            helperText={errors.description || 'Optional description for admin reference'}
            disabled={loading}
          />

          {/* Icon */}
          <TextField
            label="Icon"
            fullWidth
            value={formData.icon}
            onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
            helperText="Optional icon identifier (e.g., Material-UI icon name)"
            disabled={loading}
          />

          {/* Display Order */}
          <TextField
            label="Display Order"
            type="number"
            fullWidth
            value={formData.display_order}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, display_order: parseInt(e.target.value, 10) || 0 }))
            }
            error={!!errors.display_order}
            helperText={errors.display_order || 'Lower numbers appear first'}
            disabled={loading}
          />

          {/* Status */}
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as FilterStatus }))}
              disabled={loading}
            >
              <MenuItem value={FilterStatus.DRAFT}>Draft</MenuItem>
              <MenuItem value={FilterStatus.PUBLISHED}>Published</MenuItem>
              <MenuItem value={FilterStatus.SCHEDULED}>Scheduled</MenuItem>
              <MenuItem value={FilterStatus.UNPUBLISHED}>Unpublished</MenuItem>
            </Select>
          </FormControl>

          {/* Scheduling */}
          {formData.status === FilterStatus.SCHEDULED && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={2}>
                <DateTimePicker
                  label="Scheduled Publish Date"
                  value={formData.scheduled_publish_at ? new Date(formData.scheduled_publish_at) : null}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      scheduled_publish_at: date ? date.toISOString() : null,
                    }))
                  }
                  disabled={loading}
                />
                <DateTimePicker
                  label="Scheduled Unpublish Date (Optional)"
                  value={formData.scheduled_unpublish_at ? new Date(formData.scheduled_unpublish_at) : null}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      scheduled_unpublish_at: date ? date.toISOString() : null,
                    }))
                  }
                  disabled={loading}
                />
                {errors.scheduled_unpublish_at && (
                  <Typography variant="caption" color="error">
                    {errors.scheduled_unpublish_at}
                  </Typography>
                )}
              </Stack>
            </LocalizationProvider>
          )}

          {/* Featured Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_featured}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_featured: e.target.checked }))}
                disabled={loading}
              />
            }
            label="Featured Filter (appears prominently in UI)"
          />

          {/* Preview */}
          {formData.name && (
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Preview:
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={formData.name} color="primary" />
                <Typography variant="caption" color="text.secondary">
                  in {getCategoryName(formData.category_id)}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFilterModal;
